/**
 * Category color utilities for generating subtle, aesthetically pleasing badges
 * that match the tag styling system while preserving custom category colors
 */

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to HSL for color manipulation
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL back to RGB
 */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Generate subtle background and text colors based on category color
 * This mimics the tag system's aesthetic (e.g., bg-blue-100, text-blue-800)
 */
export function getCategoryColorClasses(categoryColor: string) {
  const rgb = hexToRgb(categoryColor);
  if (!rgb) {
    // Fallback to neutral colors if hex parsing fails
    return {
      bgColor: 'rgb(243, 244, 246)', // gray-100
      textColor: 'rgb(31, 41, 55)',  // gray-800
      borderColor: 'rgb(229, 231, 235)', // gray-200
      darkBgColor: 'rgba(31, 41, 55, 0.2)', // gray-800/20
      darkTextColor: 'rgb(209, 213, 219)', // gray-300
      darkBorderColor: 'rgb(75, 85, 99)' // gray-600
    };
  }

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  // Create light background (similar to Tailwind's -100 variants)
  const lightBg = hslToRgb(hsl.h, Math.max(hsl.s * 0.7, 30), 95);
  
  // Create dark text (similar to Tailwind's -800 variants)
  const darkText = hslToRgb(hsl.h, Math.min(hsl.s * 1.2, 90), 25);
  
  // Create border color (similar to Tailwind's -200 variants)
  const border = hslToRgb(hsl.h, Math.max(hsl.s * 0.6, 25), 88);
  
  // Dark mode colors
  const darkBg = hslToRgb(hsl.h, Math.min(hsl.s * 0.8, 60), 15);
  const darkModeText = hslToRgb(hsl.h, Math.max(hsl.s * 0.7, 40), 75);
  const darkBorder = hslToRgb(hsl.h, Math.max(hsl.s * 0.8, 50), 35);

  return {
    bgColor: `rgb(${lightBg.r}, ${lightBg.g}, ${lightBg.b})`,
    textColor: `rgb(${darkText.r}, ${darkText.g}, ${darkText.b})`,
    borderColor: `rgb(${border.r}, ${border.g}, ${border.b})`,
    darkBgColor: `rgba(${darkBg.r}, ${darkBg.g}, ${darkBg.b}, 0.2)`,
    darkTextColor: `rgb(${darkModeText.r}, ${darkModeText.g}, ${darkModeText.b})`,
    darkBorderColor: `rgb(${darkBorder.r}, ${darkBorder.g}, ${darkBorder.b})`
  };
}

/**
 * Get inline styles for category badges that match the tag aesthetic
 */
export function getCategoryBadgeStyles(categoryColor: string) {
  const colors = getCategoryColorClasses(categoryColor);
  
  return {
    backgroundColor: colors.bgColor,
    color: colors.textColor,
    borderColor: colors.borderColor,
    '--dark-bg': colors.darkBgColor,
    '--dark-text': colors.darkTextColor,
    '--dark-border': colors.darkBorderColor
  } as React.CSSProperties & Record<string, string>;
}

/**
 * Get CSS custom properties for category colors
 * Useful for more complex styling scenarios
 */
export function getCategoryCSSProperties(categoryColor: string) {
  const colors = getCategoryColorClasses(categoryColor);
  
  return {
    '--category-bg': colors.bgColor,
    '--category-text': colors.textColor,
    '--category-border': colors.borderColor,
    '--category-dark-bg': colors.darkBgColor,
    '--category-dark-text': colors.darkTextColor,
    '--category-dark-border': colors.darkBorderColor
  };
}